import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Pyramid, AlertTriangle, CheckCircle2, Database, Download, ExternalLink, Shield, FileCode, RotateCcw, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const STEPS = ['Source', 'Analysis', 'Mapping', 'Validation', 'Import plan', 'Results'];

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-border/40 bg-card/40 px-3 py-2" data-testid={`pyr-stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold ${tone || 'text-foreground'}`}>{value ?? '—'}</div>
    </div>
  );
}

export default function PyramidImportWizard({ open, onOpenChange, onImported }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState('');
  const [connections, setConnections] = useState([]);
  const [sourceId, setSourceId] = useState('');
  const [probe, setProbe] = useState(null);
  const [models, setModels] = useState([]);
  const [repoModelId, setRepoModelId] = useState('');
  const [batch, setBatch] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [plan, setPlan] = useState(null);
  const [aggMap, setAggMap] = useState({});
  const [typeMap, setTypeMap] = useState({});
  const [opMap, setOpMap] = useState({});
  const [rtMap, setRtMap] = useState({});       // relationship-type code → join_type
  const [rtCardMap, setRtCardMap] = useState({}); // relationship-type code → cardinality
  const [dupSet, setDupSet] = useState({});      // bucket → array of unique names to keep-first
  const [saveDefaults, setSaveDefaults] = useState(true);
  const [destSource, setDestSource] = useState('');
  const [modelName, setModelName] = useState('');
  const [sqlPreview, setSqlPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [verification, setVerification] = useState(null);
  const fileRef = useRef(null);

  const reset = useCallback(() => {
    setStep(0); setBusy(''); setSourceId(''); setProbe(null); setModels([]);
    setRepoModelId(''); setBatch(null); setAnalysis(null); setPlan(null);
    setAggMap({}); setTypeMap({}); setOpMap({}); setRtMap({}); setRtCardMap({}); setDupSet({}); setDestSource(''); setModelName('');
    setSqlPreview(null); setResult(null); setVerification(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    api.pyrModelConnections().then(setConnections).catch(() => setConnections([]));
  }, [open, reset]);

  const fail = (err, fallback) => toast.error(err?.message || fallback);

  const testRepo = async () => {
    setBusy('test');
    try {
      const p = await api.pyrModelTestRepo({ source_id: sourceId });
      setProbe(p);
      if (!p.ok) { toast.error('This data source does not look like a Pyramid repository.'); return; }
      const d = await api.pyrModelDiscover(sourceId);
      setModels(d.models || []);
      toast.success(`Found ${d.count} deployed Pyramid model(s)`);
    } catch (err) { fail(err, 'Could not read the repository'); } finally { setBusy(''); }
  };

  const loadAnalysis = async (bid) => {
    const a = await api.pyrModelAnalysis(bid);
    setAnalysis(a);
    setModelName((prev) => prev || a?.model?.displayName || '');
    const am = {}; (a?.mappings?.unresolvedAggregations || []).forEach((i) => { am[String(i.sourceCode)] = ''; });
    const tm = {}; (a?.mappings?.unresolvedDataTypes || []).forEach((i) => { tm[String(i.sourceCode)] = ''; });
    const om = {}; (a?.mappings?.unresolvedOperators || []).forEach((i) => { om[String(i.sourceCode)] = ''; });
    const rm = {}; (a?.mappings?.unresolvedRelationshipTypes || []).forEach((i) => { rm[String(i.sourceCode)] = ''; });
    setAggMap(am); setTypeMap(tm); setOpMap(om); setRtMap(rm); setRtCardMap({}); setDupSet({});
    return a;
  };

  const extract = async () => {
    setBusy('extract');
    try {
      const b = await api.pyrModelExtract(sourceId, repoModelId, { mode: 'create' });
      await afterBatch(b);
    } catch (err) { fail(err, 'Extraction failed'); } finally { setBusy(''); }
  };

  const afterBatch = async (b) => {
    setBatch(b);
    const p = await api.pyrModelParse(b.batch_id);
    if (!p.ok) { setStep(3); toast.error('The model definition could not be parsed.'); return; }
    await loadAnalysis(b.batch_id);
    setBatch(await api.pyrModelBatch(b.batch_id));
    setStep(1);
  };

  const uploadDefinition = async (file) => {
    if (!file) return;
    setBusy('upload');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(api.pyrModelUploadUrl(), { method: 'POST', credentials: 'include', body: fd });
      if (!resp.ok) throw new Error((await resp.text()) || 'Upload failed');
      const b = await resp.json();
      await afterBatch(b);
    } catch (err) { fail(err, 'The model definition could not be uploaded'); } finally { setBusy(''); }
  };

  const applyMappings = async () => {
    setBusy('map');
    try {
      const aggregations = {}; Object.entries(aggMap).forEach(([code, v]) => {
        if (v) aggregations[code] = { normalized: v.toUpperCase(), cloverleaf: v };
      });
      const dataTypes = {}; Object.entries(typeMap).forEach(([code, v]) => {
        if (v) dataTypes[code] = { normalized: v.toUpperCase(), cloverleaf: v };
      });
      const opCands = analysis?.mappings?.operatorCandidates || [];
      const operators = {}; Object.entries(opMap).forEach(([code, v]) => {
        if (v) {
          const cand = opCands.find((c) => c.cloverleaf === v);
          operators[code] = { normalized: cand?.normalized || v.toUpperCase(), cloverleaf: v };
        }
      });
      const rtCands = analysis?.mappings?.relationshipTypeCandidates || [];
      const relationshipTypes = {}; Object.entries(rtMap).forEach(([code, jt]) => {
        if (jt) {
          const cand = rtCands.find((c) => c.join_type === jt);
          relationshipTypes[code] = {
            normalized: cand?.normalized || jt.toUpperCase(),
            join_type: jt,
            cardinality: rtCardMap[code] || 'many_to_one',
          };
        }
      });
      await api.pyrModelPatchMappings(batch.batch_id, {
        mapping_overrides: { aggregations, dataTypes, operators, relationshipTypes },
        duplicate_resolutions: dupSet,
        save_as_default: saveDefaults,
        destination_source_id: destSource || undefined,
        model_name: modelName || undefined,
        mode: 'create',
      });
      await loadAnalysis(batch.batch_id);
      const b = await api.pyrModelBatch(batch.batch_id);
      setBatch(b);
      toast.success(saveDefaults ? 'Mappings applied and saved as tenant defaults' : 'Mappings applied');
      setStep(3);
    } catch (err) { fail(err, 'Could not apply the mappings'); } finally { setBusy(''); }
  };

  const loadPlan = async () => {
    setBusy('plan');
    try { setPlan(await api.pyrModelPlan(batch.batch_id, true)); setStep(4); }
    catch (err) { fail(err, 'Could not build the import plan'); } finally { setBusy(''); }
  };

  const approve = async (uniqueName) => {
    try {
      await api.pyrModelApproveQuery(batch.batch_id, { source_unique_name: uniqueName, approved: true });
      setPlan(await api.pyrModelPlan(batch.batch_id, true));
      toast.success('Custom query approved');
    } catch (err) { fail(err, 'Approval failed'); }
  };

  const commit = async () => {
    setBusy('commit');
    try {
      const r = await api.pyrModelCommit(batch.batch_id);
      setResult(r);
      setStep(5);
      try { setVerification(await api.pyrModelVerify(batch.batch_id)); } catch { /* non-fatal */ }
      toast.success(`Imported ${r.counts?.created || 0} object(s) into Cloverleaf`);
      onImported?.(r.model_id);
    } catch (err) { fail(err, 'The import could not be committed'); } finally { setBusy(''); }
  };

  const rollback = async () => {
    setBusy('rollback');
    try {
      const r = await api.pyrModelRollback(batch.batch_id);
      toast.success(`Rolled back ${Object.values(r.removed || {}).reduce((a, b) => a + b, 0)} object(s)`);
      setResult(null); setVerification(null); onImported?.(null);
    } catch (err) { fail(err, 'Rollback failed'); } finally { setBusy(''); }
  };

  const download = async (what, format) => {
    try {
      const resp = await fetch(api.pyrModelDownloadUrl(batch.batch_id, what, format), { credentials: 'include' });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${what}-${batch.batch_id}.${format === 'markdown' ? 'md' : format === 'json' ? 'json' : 'csv'}`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch (err) { fail(err, 'Download failed'); }
  };

  const v = analysis?.validationSummary || {};
  const items = batch?.validation?.items || [];
  const blocking = items.filter((i) => i.blocking);
  const warnings = items.filter((i) => !i.blocking);
  const counts = analysis?.counts || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="pyramid-import-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Pyramid className="w-4 h-4 text-amber-400" /> Import a semantic model from Pyramid Analytics
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-1 flex-wrap mb-3">
          {STEPS.map((s, i) => (
            <button key={s} onClick={() => i < step && setStep(i)} disabled={i > step}
              className={`text-[10px] px-2 py-1 rounded-lg transition-colors ${i === step ? 'bg-primary/20 text-primary' : i < step ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-muted-foreground'}`}
              data-testid={`pyr-step-${i}`}>
              {i + 1}. {s}
            </button>
          ))}
        </div>

        {/* 1 — Source */}
        {step === 0 && (
          <div className="space-y-3" data-testid="pyr-screen-source">
            <p className="text-xs text-muted-foreground">
              Choose a configured PostgreSQL data source that points at your Pyramid Analytics content
              repository. It is only ever read from — no write permission is needed or used.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Pyramid repository data source</Label>
              <Select value={sourceId} onValueChange={(val) => { setSourceId(val); setProbe(null); setModels([]); }}>
                <SelectTrigger className="rounded-xl" data-testid="pyr-source-select">
                  <SelectValue placeholder={connections.length ? 'Select a PostgreSQL data source' : 'No PostgreSQL data sources configured'} />
                </SelectTrigger>
                <SelectContent>
                  {connections.map((c) => (
                    <SelectItem key={c.source_id} value={c.source_id}>{c.name}{c.database ? ` · ${c.database}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={testRepo} disabled={!sourceId || busy === 'test'} className="rounded-xl" data-testid="pyr-test-repo">
              {busy === 'test' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Connect and list deployed models
            </Button>
            {probe && (
              <div className={`text-xs rounded-xl border px-3 py-2 ${probe.ok ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300' : 'border-amber-500/30 bg-amber-500/5 text-amber-300'}`} data-testid="pyr-probe">
                {probe.ok
                  ? `Repository reachable · ${probe.deployed_models ?? '?'} deployed model(s)${probe.calculation_support ? ' · calculations available' : ''}`
                  : `Missing required table(s): ${(probe.missing_required_tables || []).join(', ') || probe.error}`}
              </div>
            )}
            {models.length > 0 && (
              <div className="max-h-64 overflow-y-auto rounded-xl border border-border/40 divide-y divide-border/30" data-testid="pyr-model-list">
                {models.map((m) => (
                  <button key={m.repository_model_id} onClick={() => setRepoModelId(m.repository_model_id)}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-primary/5 ${repoModelId === m.repository_model_id ? 'bg-primary/10' : ''}`}
                    data-testid={`pyr-model-${m.repository_model_id}`}>
                    <div className="font-medium text-foreground">{m.name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {m.process_date || 'no deploy date'} · {(m.definition_length / 1024).toFixed(0)} KB
                      {m.embedded_model_id ? ` · model ${m.embedded_model_id.slice(0, 8)}` : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {repoModelId && (
              <Button onClick={extract} disabled={busy === 'extract'} className="rounded-xl bg-primary text-primary-foreground" data-testid="pyr-extract">
                {busy === 'extract' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Extract and analyse
              </Button>
            )}
            <div className="pt-2 border-t border-border/30 space-y-1.5">
              <p className="text-[11px] text-muted-foreground">
                No repository access? Import an exported <code>model_definition</code> JSON file instead.
              </p>
              <input ref={fileRef} type="file" accept=".json,application/json" className="hidden"
                onChange={(e) => { uploadDefinition(e.target.files?.[0]); e.target.value = ''; }}
                data-testid="pyr-upload-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy === 'upload'}
                className="rounded-xl" data-testid="pyr-upload-btn">
                {busy === 'upload' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload a model definition JSON
              </Button>
            </div>
          </div>
        )}

        {/* 2 — Analysis */}
        {step === 1 && analysis && (
          <div className="space-y-3" data-testid="pyr-screen-analysis">
            <div className="text-xs text-muted-foreground">
              <span className="text-foreground font-medium">{analysis.model?.displayName}</span>
              {analysis.sourceIdentity?.databaseName ? ` · ${analysis.sourceIdentity.databaseName}` : ''}
            </div>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              <Stat label="Tables" value={counts.tables} />
              <Stat label="Fields" value={counts.fields} />
              <Stat label="Measures" value={counts.measures} />
              <Stat label="Relationships" value={counts.relationships} />
              <Stat label="Hierarchies" value={counts.hierarchies} />
              <Stat label="Levels" value={counts.hierarchyLevels} />
              <Stat label="Measure groups" value={counts.measureGroups} />
              <Stat label="Custom queries" value={counts.customQueryTables} tone="text-amber-400" />
              <Stat label="Security roles" value={counts.roleAssignments} tone="text-amber-400" />
              <Stat label="Blocking items" value={v.blocking} tone={v.blocking ? 'text-rose-400' : 'text-emerald-400'} />
            </div>
            <div className="max-h-56 overflow-y-auto rounded-xl border border-border/40" data-testid="pyr-analysis-tables">
              <table className="w-full text-[11px]">
                <thead className="bg-card/60 sticky top-0"><tr>
                  {['Table', 'Type', 'Fields', 'Visible', 'Measures', 'Hierarchies'].map((h) => (
                    <th key={h} className="text-left px-2 py-1.5 text-muted-foreground font-medium">{h}</th>))}
                </tr></thead>
                <tbody>
                  {(analysis.tables || []).map((t) => (
                    <tr key={t.sourceUniqueName} className="border-t border-border/20">
                      <td className="px-2 py-1 text-foreground">{t.displayName}</td>
                      <td className="px-2 py-1">{t.isCustomQuery
                        ? <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-amber-500/20">custom query</Badge>
                        : <span className="text-muted-foreground">{t.schemaName}.{t.physicalTableName}</span>}</td>
                      <td className="px-2 py-1">{t.fields}</td>
                      <td className="px-2 py-1">{t.visible}</td>
                      <td className="px-2 py-1">{t.measures}</td>
                      <td className="px-2 py-1">{t.hierarchies}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button onClick={() => setStep(2)} className="rounded-xl" data-testid="pyr-to-mapping">Continue to mapping</Button>
          </div>
        )}

        {/* 3 — Mapping */}
        {step === 2 && analysis && (
          <div className="space-y-3" data-testid="pyr-screen-mapping">
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Cloverleaf model name</Label>
                <Input value={modelName} onChange={(e) => setModelName(e.target.value)} className="rounded-xl" data-testid="pyr-model-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Destination data connection (optional)</Label>
                <Select value={destSource} onValueChange={setDestSource}>
                  <SelectTrigger className="rounded-xl" data-testid="pyr-dest-select"><SelectValue placeholder="Choose later" /></SelectTrigger>
                  <SelectContent>{connections.map((c) => (<SelectItem key={c.source_id} value={c.source_id}>{c.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground">
              No database connection is created or changed by this import. Suggested catalog:{' '}
              <code>{analysis.model?.suggestedDatabaseName || '—'}</code>
            </p>

            {(analysis.mappings?.unresolvedAggregations || []).length === 0
              && (analysis.mappings?.unresolvedDataTypes || []).length === 0
              && (analysis.mappings?.unresolvedOperators || []).length === 0
              && (analysis.mappings?.unresolvedRelationshipTypes || []).length === 0
              && (analysis.mappings?.duplicates || []).length === 0 ? (
              <div className="text-xs rounded-xl border border-emerald-500/30 bg-emerald-500/5 text-emerald-300 px-3 py-2" data-testid="pyr-no-unmapped">
                Every Pyramid data type, aggregation, join operator and relationship is already mapped, with no duplicates to resolve.
              </div>
            ) : (
              <div className="space-y-2">
                {(analysis.mappings?.duplicates || []).map((i) => {
                  const bucketSel = dupSet[i.bucket] || [];
                  const resolved = bucketSel.includes(i.uniqueName);
                  return (
                    <div key={`dup-${i.bucket}-${i.uniqueName}`} className={`rounded-xl border px-3 py-2 space-y-1.5 ${resolved ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-rose-500/30 bg-rose-500/5'}`} data-testid={`pyr-duplicate-${i.uniqueName}`}>
                      <div className={`text-xs flex items-center gap-1.5 ${resolved ? 'text-emerald-300' : 'text-rose-300'}`}>
                        {resolved ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                        Duplicate {i.objectType.toLowerCase()} <b>{i.uniqueName}</b> — appears {i.occurrences}×
                      </div>
                      <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={resolved} data-testid={`pyr-duplicate-keepfirst-${i.uniqueName}`}
                          onChange={(e) => setDupSet((m) => {
                            const cur = new Set(m[i.bucket] || []);
                            if (e.target.checked) cur.add(i.uniqueName); else cur.delete(i.uniqueName);
                            return { ...m, [i.bucket]: Array.from(cur) };
                          })} />
                        Keep the first occurrence and drop the other {i.occurrences - 1} on import
                      </label>
                    </div>
                  );
                })}
                {(analysis.mappings?.unresolvedRelationshipTypes || []).map((i) => (
                  <div key={`rt-${i.sourceCode}`} className="rounded-xl border border-rose-500/30 bg-rose-500/5 px-3 py-2 space-y-1.5" data-testid={`pyr-unmapped-rt-${i.sourceCode}`}>
                    <div className="text-xs text-rose-300 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" /> Relationship type code <b>{String(i.sourceCode)}</b> — {i.affected} relationship(s)
                    </div>
                    <div className="text-[10px] text-muted-foreground">e.g. {i.example}</div>
                    <div className="flex items-center gap-2">
                      <Select value={rtMap[String(i.sourceCode)] || ''} onValueChange={(val) => setRtMap((m) => ({ ...m, [String(i.sourceCode)]: val }))}>
                        <SelectTrigger className="rounded-lg h-8 text-xs flex-1" data-testid={`pyr-rt-select-${i.sourceCode}`}><SelectValue placeholder="Join type" /></SelectTrigger>
                        <SelectContent>
                          {(analysis.mappings?.relationshipTypeCandidates || []).map((c) => (
                            <SelectItem key={c.join_type} value={c.join_type}>{c.label}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <Select value={rtCardMap[String(i.sourceCode)] || 'many_to_one'} onValueChange={(val) => setRtCardMap((m) => ({ ...m, [String(i.sourceCode)]: val }))}>
                        <SelectTrigger className="rounded-lg h-8 text-xs flex-1" data-testid={`pyr-rt-card-select-${i.sourceCode}`}><SelectValue placeholder="Cardinality" /></SelectTrigger>
                        <SelectContent>
                          {(analysis.mappings?.cardinalityCandidates || []).map((c) => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {(analysis.mappings?.unresolvedOperators || []).map((i) => (
                  <div key={`op-${i.sourceCode}`} className="rounded-xl border border-rose-500/30 bg-rose-500/5 px-3 py-2 space-y-1.5" data-testid={`pyr-unmapped-op-${i.sourceCode}`}>
                    <div className="text-xs text-rose-300 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" /> Join operator code <b>{String(i.sourceCode)}</b> — {i.affected} relationship condition(s)
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      The matching join stays inactive until this operator is mapped. Most Pyramid joins are equality ( = ).
                    </div>
                    <Select value={opMap[String(i.sourceCode)] || ''} onValueChange={(val) => setOpMap((m) => ({ ...m, [String(i.sourceCode)]: val }))}>
                      <SelectTrigger className="rounded-lg h-8 text-xs" data-testid={`pyr-op-select-${i.sourceCode}`}><SelectValue placeholder="Map to a join operator" /></SelectTrigger>
                      <SelectContent>
                        {(analysis.mappings?.operatorCandidates || []).map((c) => (
                          <SelectItem key={c.cloverleaf} value={c.cloverleaf}>{c.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                {(analysis.mappings?.unresolvedAggregations || []).map((i) => (
                  <div key={`agg-${i.sourceCode}`} className="rounded-xl border border-rose-500/30 bg-rose-500/5 px-3 py-2 space-y-1.5" data-testid={`pyr-unmapped-agg-${i.sourceCode}`}>
                    <div className="text-xs text-rose-300 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" /> Aggregation code <b>{String(i.sourceCode)}</b> — {i.affectedCount} measure(s)
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      e.g. {(i.examples || []).slice(0, 3).map((e) => e.displayName).join(', ')}
                    </div>
                    <Select value={aggMap[String(i.sourceCode)] || ''} onValueChange={(val) => setAggMap((m) => ({ ...m, [String(i.sourceCode)]: val }))}>
                      <SelectTrigger className="rounded-lg h-8 text-xs" data-testid={`pyr-agg-select-${i.sourceCode}`}><SelectValue placeholder="Map to a Cloverleaf aggregation" /></SelectTrigger>
                      <SelectContent>
                        {(analysis.mappings?.aggregationCandidates || []).map((c) => (
                          <SelectItem key={c.cloverleaf} value={c.cloverleaf}>{c.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                {(analysis.mappings?.unresolvedDataTypes || []).map((i) => (
                  <div key={`dt-${i.sourceCode}`} className="rounded-xl border border-rose-500/30 bg-rose-500/5 px-3 py-2 space-y-1.5" data-testid={`pyr-unmapped-type-${i.sourceCode}`}>
                    <div className="text-xs text-rose-300 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" /> Data-type code <b>{String(i.sourceCode)}</b> — {i.affectedCount} field(s)
                    </div>
                    <Select value={typeMap[String(i.sourceCode)] || ''} onValueChange={(val) => setTypeMap((m) => ({ ...m, [String(i.sourceCode)]: val }))}>
                      <SelectTrigger className="rounded-lg h-8 text-xs" data-testid={`pyr-type-select-${i.sourceCode}`}><SelectValue placeholder="Map to a Cloverleaf data type" /></SelectTrigger>
                      <SelectContent>
                        {(analysis.mappings?.cloverleafDataTypes || []).map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer">
                  <input type="checkbox" checked={saveDefaults} onChange={(e) => setSaveDefaults(e.target.checked)} data-testid="pyr-save-defaults" />
                  Save these mappings for this workspace so every future Pyramid import reuses them
                </label>
                <a href="/admin/pyramid-mappings" target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline" data-testid="pyr-open-console">
                  <ExternalLink className="w-3 h-3" /> Manage every saved mapping in the Pyramid Mapping Console
                </a>
              </div>
            )}

            <div className="rounded-xl border border-border/40 px-3 py-2 text-[11px] text-muted-foreground flex items-start gap-2" data-testid="pyr-security-preview">
              <Shield className="w-3.5 h-3.5 mt-0.5 text-amber-400" />
              <span>{analysis.security?.assignmentCount || 0} Pyramid role assignment(s) imported as a
                preview only — all unmapped, no Cloverleaf access is granted.</span>
            </div>
            <Button onClick={applyMappings} disabled={busy === 'map'} className="rounded-xl" data-testid="pyr-apply-mappings">
              {busy === 'map' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Apply and re-validate
            </Button>
          </div>
        )}

        {/* 4 — Validation */}
        {step === 3 && (
          <div className="space-y-3" data-testid="pyr-screen-validation">
            <div className="flex items-center gap-3 text-xs">
              <Badge className={blocking.length ? 'bg-rose-500/15 text-rose-400 border-rose-500/20' : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20'} data-testid="pyr-blocking-badge">
                {blocking.length} blocking
              </Badge>
              <Badge variant="outline" className="border-border/40">{warnings.length} warnings</Badge>
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1.5" data-testid="pyr-validation-items">
              {[...blocking, ...warnings].slice(0, 120).map((i, idx) => (
                <div key={`${i.code}-${idx}`} className={`rounded-lg border px-2.5 py-1.5 text-[11px] ${i.blocking ? 'border-rose-500/30 bg-rose-500/5' : 'border-border/30'}`}>
                  <span className={`font-mono mr-1.5 ${i.blocking ? 'text-rose-400' : 'text-amber-400'}`}>{i.code}</span>
                  <span className="text-foreground">{i.message}</span>
                  {i.suggestedResolution ? <div className="text-muted-foreground mt-0.5">{i.suggestedResolution}</div> : null}
                </div>
              ))}
              {!items.length && <div className="text-xs text-muted-foreground">No validation items.</div>}
            </div>
            <div className="flex gap-2">
              {blocking.length > 0 && <Button variant="outline" onClick={() => setStep(2)} className="rounded-xl" data-testid="pyr-back-to-mapping">Fix mappings</Button>}
              <Button onClick={loadPlan} disabled={blocking.length > 0 || busy === 'plan'} className="rounded-xl" data-testid="pyr-to-plan">
                {busy === 'plan' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Build the import plan
              </Button>
            </div>
          </div>
        )}

        {/* 5 — Import plan */}
        {step === 4 && plan && (
          <div className="space-y-3" data-testid="pyr-screen-plan">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              <Stat label="Create" value={plan.summary?.create} tone="text-emerald-400" />
              <Stat label="Update" value={plan.summary?.update} tone="text-sky-400" />
              <Stat label="Unchanged" value={plan.summary?.unchanged} />
              <Stat label="Skip" value={plan.summary?.skip} tone="text-amber-400" />
              <Stat label="Review" value={plan.summary?.review} tone="text-amber-400" />
              <Stat label="Conflict" value={plan.summary?.conflict} tone={plan.summary?.conflict ? 'text-rose-400' : undefined} />
            </div>
            <div className="max-h-48 overflow-y-auto rounded-xl border border-border/40" data-testid="pyr-plan-bytype">
              <table className="w-full text-[11px]">
                <thead className="bg-card/60 sticky top-0"><tr>
                  {['Object', 'Create', 'Update', 'Unchanged', 'Skip', 'Review', 'Total'].map((h) => (
                    <th key={h} className="text-left px-2 py-1.5 text-muted-foreground font-medium">{h}</th>))}
                </tr></thead>
                <tbody>
                  {Object.entries(plan.byType || {}).map(([t, b]) => (
                    <tr key={t} className="border-t border-border/20">
                      <td className="px-2 py-1 text-foreground">{t}</td>
                      <td className="px-2 py-1">{b.create}</td><td className="px-2 py-1">{b.update}</td>
                      <td className="px-2 py-1">{b.unchanged}</td><td className="px-2 py-1">{b.skip}</td>
                      <td className="px-2 py-1">{b.review}</td><td className="px-2 py-1">{b.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(plan.customQueryApprovals || []).map((q) => (
              <div key={q.source_unique_name} className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] space-y-1" data-testid={`pyr-cq-${q.source_unique_name}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-amber-300 flex items-center gap-1.5"><FileCode className="w-3.5 h-3.5" />{q.display_name} — {q.status}</span>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" className="h-6 text-[10px]" data-testid={`pyr-cq-view-${q.source_unique_name}`}
                      onClick={async () => { try { setSqlPreview(await api.pyrModelCustomQuery(batch.batch_id, q.source_unique_name)); } catch (err) { fail(err, 'Could not load the SQL'); } }}>View SQL</Button>
                    {q.status !== 'APPROVED' && (
                      <Button size="sm" className="h-6 text-[10px]" onClick={() => approve(q.source_unique_name)} data-testid={`pyr-cq-approve-${q.source_unique_name}`}>Approve</Button>)}
                  </div>
                </div>
                <div className="text-muted-foreground">Imported inactive. SQL is never executed during preview.</div>
              </div>
            ))}
            {sqlPreview && (
              <pre className="rounded-xl bg-slate-950/80 text-emerald-300 text-[10px] p-3 overflow-x-auto max-h-40" data-testid="pyr-sql-preview">{sqlPreview.sql}</pre>
            )}
            <Button onClick={commit} disabled={!plan.summary?.can_commit || busy === 'commit'} className="rounded-xl bg-primary text-primary-foreground" data-testid="pyr-commit">
              {busy === 'commit' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Import into Cloverleaf
            </Button>
          </div>
        )}

        {/* 6 — Results */}
        {step === 5 && (
          <div className="space-y-3" data-testid="pyr-screen-results">
            {result ? (
              <>
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-300" data-testid="pyr-result-banner">
                  Imported into Cloverleaf model <code>{result.model_id}</code>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Stat label="Created" value={result.counts?.created} tone="text-emerald-400" />
                  <Stat label="Updated" value={result.counts?.updated} tone="text-sky-400" />
                  <Stat label="Skipped" value={result.counts?.skipped} tone="text-amber-400" />
                  <Stat label="Crosswalk rows" value={result.crosswalk_entries} />
                </div>
                {verification && (
                  <div className={`rounded-xl border px-3 py-2 text-[11px] ${verification.ok ? 'border-emerald-500/30 text-emerald-300' : 'border-amber-500/30 text-amber-300'}`} data-testid="pyr-verification">
                    Verification {verification.ok ? 'passed' : 'found differences'} —{' '}
                    {(verification.checks || []).map((c) => `${c.object} ${c.actual}/${c.expected}`).join(' · ')}
                  </div>
                )}
                {(result.errors || []).map((e, i) => (
                  <div key={i} className="text-[11px] text-amber-300">{e.error}</div>))}
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => download('report', 'markdown')} data-testid="pyr-dl-report"><Download className="w-3.5 h-3.5 mr-1.5" />Migration report</Button>
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => download('crosswalk', 'csv')} data-testid="pyr-dl-crosswalk"><Download className="w-3.5 h-3.5 mr-1.5" />Crosswalk CSV</Button>
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => download('inventory', 'csv')} data-testid="pyr-dl-inventory"><Download className="w-3.5 h-3.5 mr-1.5" />Object inventory</Button>
                  <Button size="sm" className="rounded-xl" onClick={() => { onOpenChange(false); onImported?.(result.model_id, true); }} data-testid="pyr-open-model"><ExternalLink className="w-3.5 h-3.5 mr-1.5" />Open the model</Button>
                  <Button variant="ghost" size="sm" className="rounded-xl text-rose-400" onClick={rollback} disabled={busy === 'rollback'} data-testid="pyr-rollback"><RotateCcw className="w-3.5 h-3.5 mr-1.5" />Roll back this import</Button>
                </div>
              </>
            ) : (
              <div className="text-xs text-muted-foreground" data-testid="pyr-rolled-back">This import has been rolled back. No Cloverleaf objects remain from it.</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
